import React, { useEffect } from 'react'

import Icon from '../../atoms/Icon/Icon'

interface AccordionProps {
  id: string
  open: boolean
  setOpen: (open: boolean) => void
  summaryOverlayHeader?: React.ReactNode // Optional header for mobile full-screen accordion
  summary: React.ReactNode
  summaryFooter?: React.ReactNode // Optional footer for additional details
  children: React.ReactNode
  className?: string
  fullScreenOverlay?: boolean
  isDisabled?: boolean
}

const Accordion: React.FC<AccordionProps> = ({
  id,
  open = false,
  setOpen,
  summaryOverlayHeader,
  summary,
  summaryFooter,
  children,
  className = '',
  fullScreenOverlay = false, // inline accordion by default
  isDisabled = false
}) => {
  const renderChildren = () => {
    if (open) {
      if (fullScreenOverlay) {
        return (
          <>
            {/* Full Screen overlay layout*/}
            <div
              id={`mobile-children-${id}`}
              className={`
                fixed top-18 left-0 right-0 z-10 md:hidden bg-background-grey max-h-[91%] 
                overflow-y-auto ${open ? 'animate-slide-in-left' : 'animate-slide-out-left'}
              `}
            >
              {summaryOverlayHeader && <div className="px-2 mt-2">{summaryOverlayHeader}</div>}
              <div className="px-2 mb-6">{children}</div>
            </div>
            <div id={`desktop-children-${id}`} className="hidden md:block">
              {children}
            </div>
          </>
        )
      } else {
        // Inline view
        return <div id={`inline-children-${id}`}>{children}</div>
      }
    }

    return <></>
  }

  // Disable parent scroll when fullScreenOverlay is open (mobile)
  useEffect(() => {
    const isMobile = window.innerWidth < 802

    // Handle back button
    const handleBackButton = () => {
      if (open && fullScreenOverlay) {
        setOpen(false)
      }
    }

    // Handle scroll lock
    if (open && fullScreenOverlay && isMobile) {
      document.documentElement.style.overflow = 'hidden'
    } else {
      document.documentElement.style.overflow = ''
    }

    window.addEventListener('popstate', handleBackButton)

    return () => {
      document.documentElement.style.overflow = ''
      window.removeEventListener('popstate', handleBackButton)
    }
  }, [open, fullScreenOverlay, setOpen])

  return (
    <>
      {/* Overlay for mobile full-screen, only if fullScreenOverlay is true */}
      {open && fullScreenOverlay && (
        <div id={`overlay-${id}`} className="fixed inset-0 z-10 bg-background-grey bg-opacity-40 md:hidden" />
      )}
      <div id={`accordion-${id}`} className={`${className}`}>
        <div id={`accordion-inner-div-${id}`} className="px-4 py-[18px] md:p-6" onClick={() => setOpen(!open)}>
          {/* Summary row: summary and arrow icon*/}
          <div id={`accordion-summary-${id}`} className="w-full flex items-center justify-between">
            {summary}

            {/* Right arrow for mobile: only show if fullScreenOverlay is true */}
            {!isDisabled && (
              <>
                <Icon
                  name="arrow-right"
                  className={`icon-chevron-right text-24px 
                ${fullScreenOverlay ? 'block md:hidden' : 'hidden'} `}
                />

                {/* Down arrow for desktop */}
                <Icon
                  name="arrow-down"
                  className={`icon-chevron-down text-24px ${open ? ' rotate-180' : ''}
               ${fullScreenOverlay ? 'hidden md:block' : ''} transition-transform duration-200`}
                />
              </>
            )}
          </div>
          {/* summaryFooter: summary details row below summary+arrow */}
          {summaryFooter && <div id={`accordion-summary-footer-${id}`}>{summaryFooter}</div>}
        </div>

        {!isDisabled && renderChildren()}
      </div>
    </>
  )
}

export default Accordion
